import { UserInvoicesAndReceiptsWidgetFilterFieldProvider } from './../../../invoices-and-receipts-widget/filters/user-invoices-and-receipts-widget-filter-field-provider';
import { FilterFieldProvider } from './../../../../common/filter/filter-field-provider';
import { createFeatureSelector, createSelector } from "@ngrx/store";
import { IColumnsState, ITableState } from "../models/table";
import { State, tableAdapter, TableInstance } from "./table.reducers";
import * as userSettings from "@user-settings/selectors/user-settings";
import { UserDebtorAccountHistoryWidgetFilterFieldProvider } from "@feature/_app_modules/debtor-account-history-widget/filters/user-debtor-account-history-widget-filter-field-provider";
import * as widgetConfigSelectors from "@widget-configs/selectors/widget-configs";
import { WidgetConfiguration } from "@feature/widget-configs";
import * as filterSelectors from "@common/filter/selectors/filter.selectors";
import { WidgetFilterInstance } from '@common/filter/reducers/filter.reducers';
import { IRow, ITable } from '@common/models/table';
import lodash from 'lodash';
import { RuleSet } from 'ngx-angular-query-builder';
import { andFilters } from '@common/filter/filter-utils';
import { QueryBuilderConfigProvider } from '@common/filter/query-builder-config-provider';
import { StandardFilterStrategy } from '@common/filter/standard-filter-strategy';
import { CallsWidgetFilterFieldProvider } from '@feature/_app_modules/mojo-calls-widget/filters/mojo-calls-widget-filter-field-provider';
import { ColumnDefinitionFormatType as FormatType } from '@feature/common/enums/column-definition-format-type';
import { HeadLeaseWidgetFilterFieldProvider } from '@feature/head-lease-schedule/filters/Head-Lease-Schedule-widget-filter-field-provider';

export const selectTableState = createFeatureSelector<State>("table");

export const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
} = tableAdapter.getSelectors(selectTableState);

export const selectEntityById = (id: string) =>
  createSelector(selectEntities, (entities) => entities[id]);

export const selectIsLoading = (id: string) =>
  createSelector([selectEntityById(id)], (entity: TableInstance) => {
    return !entity || entity.loadingTable || entity.loadingColumns;
  });

export const selectTable = (id: string) =>
  createSelector([selectEntityById(id)], (entity: TableInstance) => {
    if (entity) {
      return {
        table: entity.table,
        isLoading: entity.loadingTable,
      } as ITableState;
    }
    return null;
  });

  export const selectAllValuesFromOneColumnAsArray = (id: string, widgetConfigId: number, fieldName: string) =>
    createSelector([selectWidgetTable(id, widgetConfigId)], (entity: ITableState) => {
      if (entity?.table) {
        return entity.table.Rows.map(x=> x[fieldName])
      }
      return [];
    });



export const selectWidgetTable = (id: string, widgetConfigId: number) =>
  createSelector([
    selectEntityById(id),
    filterFieldProvider(id, widgetConfigId),
    filterSelectors.selectByWidgetId(id),
    widgetConfigSelectors.selectEntityByWidgetConfigId(widgetConfigId)
  ],
    (entity: TableInstance,
      filterFieldProvider: FilterFieldProvider,
      filter: WidgetFilterInstance,
      widgetConfig: WidgetConfiguration) => {
      if (entity.table) {
        if (!filterFieldProvider) {
          return {
            table: entity.table,
            isLoading: entity.loadingTable,
          } as ITableState;
        }
        const table = lodash.cloneDeep(entity.table) as ITable;
        const userFilter: RuleSet = filter ? filter.filter : null;
        const sysAdminFilter: RuleSet = widgetConfig.filter;

        const allFilters = andFilters(
          userFilter,
          sysAdminFilter
        );

        const fs = new StandardFilterStrategy<IRow>(filterFieldProvider);
        const rows = fs.applyFilter(entity.table.Rows, allFilters);
        table.Rows = rows;

        return {
          table: table,
          isLoading: entity.loadingTable,
        } as ITableState;
      }
      return null;
    });

export const selectSummedFooter = (id: string, widgetConfigId: number) =>
  createSelector([
    selectWidgetTable(id, widgetConfigId)
  ],
    (entity: ITableState) => {
      const result = {};
      if (entity?.table?.Columns) {
        entity.table.Columns.forEach(col => {
          if (col.Type == FormatType.Currency)
            result[col.Name] = entity.table.Rows.reduce((sum, x) => sum + x[col.Name], 0)
        }
        )
      }
      return result;
    });

export const selectColumns = (id: string) =>
  createSelector([selectEntityById(id)], (entity: TableInstance) => {
    if (entity) {
      return {
        columns: entity.columns,
        isLoading: entity.loadingColumns,
      } as IColumnsState;
    }
    return null;
  });

  export const selectAllColumns = (id: string) =>
  createSelector([selectEntityById(id)], (entity: TableInstance) => {
    if (entity) {
      return {
        columns: entity.allColumns,
        isLoading: entity.loadingAllColumns,
      } as IColumnsState;
    }
    return null;
  });


const filterFieldProvider = (id: string, widgetConfigId: number) =>
  createSelector([
    selectEntityById(id),
    userSettings.getCurrentProduct,
    widgetConfigSelectors.selectEntityByWidgetConfigId(widgetConfigId)
  ],
    (table: TableInstance, product: string, widgetConfig: WidgetConfiguration) => {
      if (table?.table && widgetConfig?.widgetTypeId) {
        return getFilterFieldProviderInstance(widgetConfig.widgetTypeId, table.table, product);
      }
      return undefined;
    });

export const queryBuilderConfig = (widgetId: string, widgetConfigId: number) =>
  createSelector(
    filterFieldProvider(widgetId, widgetConfigId),
    (ffp) => {
      if (ffp) {
        const configBuilder = new QueryBuilderConfigProvider(ffp);
        return configBuilder.getQueryBuilderConfig();
      }
      return undefined;
    },
  );

const getFilterFieldProviderInstance = (widgetTypeId: string, table: ITable, product: string) => {
  switch (widgetTypeId) {
    case "MojoDebtorAccountHistory":
    case "MftDebtorAccountHistory":
    case "MciDebtorAccountHistory":
      return new UserDebtorAccountHistoryWidgetFilterFieldProvider(table, product);
    case "CallsWidget":
      return new CallsWidgetFilterFieldProvider(table, product);
    case "InvoicesAndReceiptsWidget":
    case "MojoInvoicesAndReceiptsWidget":
      return new UserInvoicesAndReceiptsWidgetFilterFieldProvider(table);
    case "HeadLeaseScheduleWidget":
      return new HeadLeaseWidgetFilterFieldProvider(table, product)
    default:
      return undefined;
  }
};

